import { Box, Button, Typography } from '@mui/material';
import { memo } from 'react';
import styles from './BasicInfo.module.scss';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { ICustomer, ICustomerField } from '~/utils/interface/customer';

const cx = classNames.bind(styles);

type CustomerListProps = {
  data: ICustomer | undefined;
  openEditDialog?: () => void;
};

const BasicInfo = (props: CustomerListProps) => {
  const { t } = useTranslation('admin/customer');
  const { data } = props;

  const renderCustomerField = (_item: ICustomerField, index: number) => {
    return (
      <Box className={cx('customer-field-item')} key={index}>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {_item?.name}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {_item?.value}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={cx('basicInfo-container')}>
      <Box className='flex justify-end'>
        <Button
          className='w-[123px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
          color='primary'
          variant='contained'
          onClick={props.openEditDialog}
        >
          {t('edit', { ns: 'admin/common' })}
        </Button>
      </Box>
      <Box className={cx('information-container')}>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('properties.firstName')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.firstName}
          </Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('properties.lastName')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.lastName}
          </Typography>
        </Box>
        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.type')}
          </Typography>
          <Typography variant='body2' className={`${cx('header-title-item')} capitalize`}>
            {data?.customerTypeDetail}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.email')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.email}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.phone')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.phone}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.salePerson')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.account?.name}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.status')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.status}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.address')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.address}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.address2')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.address2}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.city')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.city}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.state')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.state}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.zipCode')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.zipCode}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.country')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.country}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('properties.jobTitle')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.jobTitle}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('properties.crmContact')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.isCrm ? 'Yes' : 'No'}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('basicInfo.organization')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.parent?.name}
          </Typography>
        </Box>

        <Box className={cx('info')}>
          <Typography variant='body2' className={cx('header-title')}>
            {t('properties.referralResource')}
          </Typography>
          <Typography variant='body2' className={cx('header-title-item')}>
            {data?.referralSource}
          </Typography>
        </Box>

        {data?.customerFields?.map((item: ICustomerField, index: number) => renderCustomerField(item, index))}
      </Box>
    </Box>
  );
};

export default memo(BasicInfo);
